import { DatabaseSchema } from '../types';
import { getSessionToken } from './auth';
import {
  CURRENT_SEED_VERSION,
  SEED_DATE,
  seededBloodPressure,
  seededBodyMeasurement,
  seededLabResults,
  seededRiskSnapshot,
  seededWeight,
} from './healthSeed';

const DEV_DATABASE_KEY = 'weight_tracker_dev_db';

export const isProduction = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname !== 'localhost' && hostname !== '127.0.0.1' && !hostname.includes('localhost');
};

export const getInitialDatabase = (): DatabaseSchema => ({
  weights: [seededWeight()],
  goals: {
    initialWeight: 142.2,
    startWeight: 142.2,
    targetWeight: 99.9,
    currentWeight: 142.2,
    startDate: SEED_DATE,
    targetDate: '',
    weeklyTarget: 0.5
  },
  preferences: {
    height: 168.5,
    initialWeight: 142.2,
    goalWeight: 99.9,
    monthlyWeightLossTarget: 4.0,
    startDate: SEED_DATE,
    units: { weight: 'kg', height: 'cm' },
    showGym: false,
    weeklyActivityMinutesTarget: 150,
    targetMode: 'weekly_minutes',
    bpTarget: { systolic: 135, diastolic: 85 },
    medicalContext: {
      conditions: ['Tinnitus', 'ADHD', 'Depression', 'Cerebral dysrhythmia (childhood)', 'Heart murmur (childhood)', 'Epilepsy (childhood)'],
      familyHistory: ['Mother: type 2 diabetes', 'Mother: depression', 'Father: unknown'],
      allergies: [],
    },
  },
  meals: [],
  exercises: [],
  supplements: [],
  bodyMeasurements: [seededBodyMeasurement()],
  photos: [],
  bloodPressure: [seededBloodPressure()],
  labResults: seededLabResults(),
  walks: [],
  healthRiskSnapshots: [seededRiskSnapshot()],
  foodLog: [],
  metadata: {
    version: '2.0.0',
    lastUpdated: new Date().toISOString(),
    totalEntries: 0,
    seedVersion: CURRENT_SEED_VERSION,
  }
});

// Backfill missing fields on databases created by the pre-revamp schema.
// Idempotent: safe to run on every load.
export const migrateDatabase = (db: Partial<DatabaseSchema> & Record<string, any>): DatabaseSchema => {
  const initial = getInitialDatabase();
  const migrated: DatabaseSchema = {
    weights: db.weights ?? [],
    meals: (db.meals ?? []).map(({ orlistatTaken: _drop, ...rest }: any) => rest),
    exercises: db.exercises ?? [],
    supplements: db.supplements ?? [],
    bodyMeasurements: db.bodyMeasurements ?? [],
    photos: db.photos ?? [],
    bloodPressure: db.bloodPressure ?? [],
    labResults: db.labResults ?? [],
    walks: db.walks ?? [],
    healthRiskSnapshots: db.healthRiskSnapshots ?? [],
    foodLog: db.foodLog ?? [],
    goals: db.goals ?? initial.goals,
    preferences: db.preferences
      ? {
          ...initial.preferences!,
          ...db.preferences,
          bpTarget: db.preferences.bpTarget ?? initial.preferences!.bpTarget,
          medicalContext: db.preferences.medicalContext ?? initial.preferences!.medicalContext,
          showGym: db.preferences.showGym ?? false,
          weeklyActivityMinutesTarget: db.preferences.weeklyActivityMinutesTarget ?? 150,
          targetMode: db.preferences.targetMode ?? 'weekly_minutes',
        }
      : initial.preferences,
    workoutTemplates: db.workoutTemplates,
    customRecipes: db.customRecipes,
    recipeUsage: db.recipeUsage,
    metadata: {
      version: '2.0.0',
      lastUpdated: new Date().toISOString(),
      totalEntries: db.metadata?.totalEntries ?? 0,
      seedVersion: db.metadata?.seedVersion ?? 0,
    },
  };

  // First-time seed of the Aug 2025 baseline (runs once when seedVersion < CURRENT)
  if ((migrated.metadata.seedVersion ?? 0) < CURRENT_SEED_VERSION) {
    if (!migrated.weights.some((w) => w.date === SEED_DATE)) migrated.weights.push(seededWeight());
    if (!migrated.bodyMeasurements.some((m) => m.date === SEED_DATE)) migrated.bodyMeasurements.push(seededBodyMeasurement());
    if (!migrated.bloodPressure.some((b) => b.date === SEED_DATE)) migrated.bloodPressure.push(seededBloodPressure());
    if (!migrated.healthRiskSnapshots.some((r) => r.date === SEED_DATE)) migrated.healthRiskSnapshots.push(seededRiskSnapshot());
    if (!migrated.labResults.some((l) => l.date === SEED_DATE)) migrated.labResults.push(...seededLabResults());
    migrated.metadata.seedVersion = CURRENT_SEED_VERSION;
  }

  return migrated;
};

export const saveDatabase = async (data: DatabaseSchema): Promise<void> => {
  try {
    if (isProduction()) {
      const sessionToken = getSessionToken();
      const response = await fetch('/api/database-import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': sessionToken || '',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to save to production database: ${response.statusText}`);
      }
    } else {
      localStorage.setItem(DEV_DATABASE_KEY, JSON.stringify(data));
    }
  } catch (error) {
    console.error('Error saving database:', error);
    throw error;
  }
};

export const loadDatabase = async (): Promise<DatabaseSchema> => {
  try {
    if (isProduction()) {
      const sessionToken = getSessionToken();
      const response = await fetch('/api/database-export', {
        headers: {
          'X-Session-Token': sessionToken || '',
        },
      });
      
      if (!response.ok) {
        // If authentication failed, clear invalid tokens
        if (response.status === 401) {
          console.warn('Authentication failed, clearing tokens');
          localStorage.removeItem('stride_auth_token');
          localStorage.removeItem('stride_auth_timestamp');
          localStorage.removeItem('stride_authenticated');
        }
        console.warn('Failed to load from production database, using initial data');
        return getInitialDatabase();
      }
      
      const data = await response.json();
      return data ? migrateDatabase(data) : getInitialDatabase();
    } else {
      const stored = localStorage.getItem(DEV_DATABASE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const migrated = migrateDatabase(parsed);
        localStorage.setItem(DEV_DATABASE_KEY, JSON.stringify(migrated));
        return migrated;
      }

      const initialData = getInitialDatabase();
      localStorage.setItem(DEV_DATABASE_KEY, JSON.stringify(initialData));
      return initialData;
    }
  } catch (error) {
    console.error('Error loading database:', error);
    return getInitialDatabase();
  }
};

export const clearDatabase = async (): Promise<void> => {
  try {
    if (isProduction()) {
      const sessionToken = getSessionToken();
      const response = await fetch('/api/database-clear', {
        method: 'POST',
        headers: {
          'X-Session-Token': sessionToken || '',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to clear production database: ${response.statusText}`);
      }
    } else {
      localStorage.removeItem(DEV_DATABASE_KEY);
    }
  } catch (error) {
    console.error('Error clearing database:', error);
    throw error;
  }
};

export const exportDatabase = async (): Promise<string> => {
  try {
    const data = await loadDatabase();
    return JSON.stringify(data, null, 2);
  } catch (error) {
    console.error('Error exporting database:', error);
    throw error;
  }
};

export const importDatabase = async (jsonData: string): Promise<void> => {
  try {
    const data = JSON.parse(jsonData);
    await saveDatabase(data);
  } catch (error) {
    console.error('Error importing database:', error);
    throw error;
  }
};